import React, { useState, useEffect } from "react";
// @material-ui/core components
import { makeStyles } from "@material-ui/core/styles";
import { useTheme } from "@material-ui/core/styles";
import Box from "@material-ui/core/Box";
import Container from "@material-ui/core/Container";
import Grid from "@material-ui/core/Grid";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Divider from "@material-ui/core/Divider";
import Chip from "@material-ui/core/Chip";
import InputAdornment from "@material-ui/core/InputAdornment";
import CircularProgress from "@material-ui/core/CircularProgress";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import IconButton from "@material-ui/core/IconButton";
import Person from "@material-ui/icons/Person";
import Search from "@material-ui/icons/Search";
import Add from "@material-ui/icons/Add";
import Remove from "@material-ui/icons/Remove";
import DeleteOutline from "@material-ui/icons/DeleteOutline";
import PersonAdd from "@material-ui/icons/PersonAdd";

// Icons & Network
import DoobiHeader from "components/Headers/DoobiHeader";
import config from "../../../config";

const useStyles = makeStyles((theme) => ({
    cardRoot: {
        marginBottom: "1.5rem",
    },
    itemCard: {
        cursor: "pointer",
        transition: "all 0.2s",
        border: "2px solid transparent",
        borderRadius: "12px",
        height: "100%",
        "&:hover": {
            borderColor: theme.palette.primary.main,
            transform: "translateY(-2px)",
            boxShadow: "0 4px 20px 0 rgba(0,0,0,.12)",
        },
    },
    itemIcon: {
        width: "50px",
        height: "50px",
        objectFit: "contain",
        marginRight: "12px"
    },
    searchField: {
        marginBottom: "1.5rem",
    },
    serviceChip: {
        margin: "8px",
        fontWeight: "600",
        padding: "12px 8px",
        height: "auto",
        borderRadius: "10px",
        fontSize: "0.95rem",
        cursor: "pointer",
        transition: "all 0.2s",
        "&:hover": {
            backgroundColor: theme.palette.primary.main + " !important",
            color: "#fff !important",
            borderColor: theme.palette.primary.main,
            "& .MuiChip-label": {
                color: "#fff",
            }
        }
    },
    paymentButton: {
        margin: "0 5px",
        minWidth: "80px",
        fontWeight: "bold",
    },
    basketItem: {
        borderBottom: "1px solid #f2f2f2",
        padding: "12px 0",
        "&:last-child": {
            borderBottom: "none"
        }
    },
    qtyBtn: {
        padding: "4px",
        border: "1px solid #e9ecef",
        borderRadius: "4px",
        margin: "0 8px"
    },
    fullHeightCard: {
        height: "calc(100vh - 140px)",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        backgroundColor: "#fff",
        borderRadius: "15px",
        boxShadow: "0 0 2rem 0 rgba(136, 152, 170, .15)",
    },
    scrollableContent: {
        flex: 1,
        overflowY: "auto",
        paddingRight: "10px",
        minHeight: 0,
        "&::-webkit-scrollbar": {
            width: "5px",
        },
        "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#e9ecef",
            borderRadius: "10px",
        },
    },
    selectionBlock: {
        padding: "12px",
        borderRadius: "8px",
        border: "1px dashed #ced4da",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        marginBottom: "1rem",
        transition: "all 0.2s",
        "&:hover": {
            borderColor: theme.palette.primary.main,
            backgroundColor: "#f8f9fe"
        }
    }
}));

function Pos() {
    const classes = useStyles();
    const theme = useTheme();

    const [allServices, setAllServices] = useState([]);
    const [globalServiceTypes, setGlobalServiceTypes] = useState([]);
    const [items, setItems] = useState([]); // Main items with servicesById
    const [searchQuery, setSearchQuery] = useState("");
    const [isLoading, setIsLoading] = useState(true);
    const [errorMsg, setErrorMsg] = useState("");

    // Dialog state for selecting service type
    const [selectedItem, setSelectedItem] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);

    const [basket, setBasket] = useState([]);
    const [discount, setDiscount] = useState(0);
    const [deliveryCharge, setDeliveryCharge] = useState(0);

    // Customer & Address State
    const [allUsers, setAllUsers] = useState([]);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [selectedAddress, setSelectedAddress] = useState(null);
    const [customerDialogOpen, setCustomerDialogOpen] = useState(false);
    const [addressDialogOpen, setAddressDialogOpen] = useState(false);
    const [userSearchQuery, setUserSearchQuery] = useState("");

    // Unified Customer Form State
    const [customerForm, setCustomerForm] = useState({
        mobileNumber: "",
        firstName: "",
        lastName: "",
        emailAddress: "",
        building: "",
        road: "",
        block: "",
        districtArea: "",
        flatOfficeNo: "",
        shortAddress: ""
    });

    const resetCustomerForm = () => {
        setCustomerForm({
            mobileNumber: "",
            firstName: "",
            lastName: "",
            emailAddress: "",
            building: "",
            road: "",
            block: "",
            districtArea: "",
            flatOfficeNo: "",
            shortAddress: ""
        });
    };

    useEffect(() => {
        let isMounted = true;

        const fetchServices = async () => {
            try {
                const token = localStorage.getItem("token");
                const selectedCountry = localStorage.getItem("selectedCountry");
                const tenantId = localStorage.getItem("tenantId");

                const apiUrl = `${config.WS_BASE_URL}app/service/active`;

                const fetchResponse = await fetch(apiUrl, {
                    method: 'GET',
                    headers: {
                        'Authorization': token || '',
                        'Country': selectedCountry || '',
                        'Content-Type': 'application/json',
                        'X-Tenant-Key': tenantId || 'main'
                    }
                });

                const responseData = await fetchResponse.json();

                if (!isMounted) return;

                if (!responseData) {
                    throw new Error("No response data received from API");
                }

                const data = responseData.data || [];
                setAllServices(data);

                // Items are directly in the data array
                // Each item has servicesById array with available service types
                const sortedItems = [...data].sort((a, b) => (a.orderNo || 0) - (b.orderNo || 0));
                setItems(sortedItems);

            } catch (error) {
                console.error("POS: Error in fetchServices:", error);
                if (isMounted) setErrorMsg(error.message || "Failed to load services");
            } finally {
                if (isMounted) {
                    setIsLoading(false);
                }
            }
        };

        const fetchServiceTypes = async () => {
            try {
                const token = localStorage.getItem("token");
                const selectedCountry = localStorage.getItem("selectedCountry");
                const tenantId = localStorage.getItem("tenantId");

                const response = await fetch(`${config.WS_BASE_URL}app/service-type`, {
                    method: 'GET',
                    headers: {
                        'Authorization': token || '',
                        'Country': selectedCountry || '',
                        'Content-Type': 'application/json',
                        'X-Tenant-Key': tenantId || 'main'
                    }
                });

                const responseData = await response.json();
                if (!isMounted) return;

                const data = Array.isArray(responseData?.data)
                    ? responseData.data
                    : Array.isArray(responseData)
                        ? responseData
                        : [];

                setGlobalServiceTypes(data);
            } catch (error) {
                console.error("POS: Error in fetchServiceTypes:", error);
            }
        };

        const fetchUsers = async () => {
            try {
                const token = localStorage.getItem("token");
                const selectedCountry = localStorage.getItem("selectedCountry");
                const tenantId = localStorage.getItem("tenantId");

                const response = await fetch(config.WS_BASE_URL + "app/user", {
                    headers: {
                        'Authorization': token || '',
                        'Country': selectedCountry || '1',
                        'Content-Type': 'application/json',
                        'X-Tenant-Key': tenantId || 'main'
                    }
                });
                const data = await response.json();
                if (data && data.data) {
                    if (isMounted) setAllUsers(data.data);
                }
            } catch (err) {
                console.error("Error fetching users:", err);
            }
        };

        fetchServices();
        fetchServiceTypes();
        fetchUsers();

        return () => {
            isMounted = false;
        };
    }, []);

    const parsePrice = (value) => {
        const parsed = parseFloat(value);
        return Number.isFinite(parsed) ? parsed : 0;
    };

    const isPerItemService = (service) =>
        service?.isPerItem === 1 || service?.isPerItem === true || service?.isPerItem === "1";

    const isCategoryOnlyService = (service) =>
        service?.isCategoryOnly === 1 || service?.isCategoryOnly === true || service?.isCategoryOnly === "1";

    const getGlobalServiceType = (serviceTypeId) => {
        if (serviceTypeId == null) {
            return null;
        }

        return globalServiceTypes.find((type) => String(type.id) === String(serviceTypeId)) || null;
    };

    const getServiceTypeName = (serviceType) => {
        const globalType = getGlobalServiceType(serviceType?.serviceTypeId ?? serviceType?.id);
        const rawName =
            serviceType?.serviceType?.nameEn ||
            serviceType?.serviceTypeByServiceTypeId?.nameEn ||
            globalType?.nameEn ||
            serviceType?.nameEn ||
            serviceType?.name ||
            "";

        const cleanName = rawName.split("(")[0].trim();
        return cleanName || "Service Type";
    };

    const getChildServicesForCategory = (item) => {
        if (!isCategoryOnlyService(item) || !Array.isArray(item?.servicesById)) {
            return [];
        }

        return item.servicesById.map((childService) => ({
            ...childService,
            parentCategoryId: item.id,
            parentCategoryNameEn: item.nameEn,
            parentCategoryNameAr: item.nameAr
        }));
    };

    const getServiceTypesForItem = (item) => {
        if (!item) {
            return [];
        }

        const pricingEntries = Array.isArray(item.servicePricings) ? item.servicePricings : [];
        if (pricingEntries.length > 0) {
            return pricingEntries
                .map((pricing) => {
                    const linkedServiceType = pricing?.serviceType || pricing?.serviceTypeByServiceTypeId || {};
                    const serviceTypeId = pricing?.serviceTypeId ?? linkedServiceType.id ?? pricing?.id;
                    const resolvedPrice = parsePrice(pricing?.price ?? pricing?.bagPrice);

                    if (serviceTypeId == null || resolvedPrice <= 0) {
                        return null;
                    }

                    return {
                        id: serviceTypeId,
                        serviceTypeId,
                        nameEn: linkedServiceType.nameEn || pricing?.nameEn || "",
                        nameAr: linkedServiceType.nameAr || pricing?.nameAr || "",
                        price: resolvedPrice,
                        raw: pricing,
                        source: "servicePricings"
                    };
                })
                .filter(Boolean);
        }

        const legacyEntries = Array.isArray(item.servicesById) ? item.servicesById : [];
        return legacyEntries
            .map((serviceType) => {
                const resolvedPrice = parsePrice(serviceType?.price ?? serviceType?.bagPrice ?? item?.bagPrice ?? item?.price);

                if ((serviceType?.id == null && serviceType?.serviceTypeId == null) || resolvedPrice <= 0) {
                    return null;
                }

                return {
                    id: serviceType?.id,
                    serviceTypeId: serviceType?.serviceTypeId ?? serviceType?.id,
                    nameEn: serviceType?.nameEn || serviceType?.serviceType?.nameEn || "",
                    nameAr: serviceType?.nameAr || serviceType?.serviceType?.nameAr || "",
                    price: resolvedPrice,
                    raw: serviceType,
                    source: "servicesById"
                };
            })
            .filter(Boolean);
    };

    const displayItems = (() => {
        const seenItemIds = new Set();

        return items
            .flatMap((item) => {
                if (!isPerItemService(item)) {
                    return [];
                }

                if (isCategoryOnlyService(item)) {
                    return getChildServicesForCategory(item);
                }

                return [item];
            })
            .filter((item) => {
                const itemKey = String(item?.id ?? "");
                if (!itemKey || seenItemIds.has(itemKey)) {
                    return false;
                }

                seenItemIds.add(itemKey);
                return getServiceTypesForItem(item).length > 0;
            });
    })();

    const getItemCategoryLabel = (item) => {
        if (item?.parentCategoryNameEn) {
            return item.parentCategoryNameEn;
        }

        if (item?.mainServiceId != null) {
            const parentService = allServices.find((service) => String(service.id) === String(item.mainServiceId));
            if (parentService?.nameEn) {
                return parentService.nameEn;
            }
        }

        return "Items";
    };

    const getItemServiceTypeLabels = (item) => {
        const seenLabels = new Set();

        return getServiceTypesForItem(item)
            .map((serviceType) => getServiceTypeName(serviceType))
            .filter((label) => {
                if (!label || seenLabels.has(label)) {
                    return false;
                }

                seenLabels.add(label);
                return true;
            });
    };

    // Filter items based on search query and isPerItem=1
    const filteredItems = displayItems.filter(item => {
        const normalizedSearch = searchQuery.toLowerCase();
        const matchesSearch = item.nameEn?.toLowerCase().includes(normalizedSearch) ||
            item.nameAr?.includes(searchQuery) ||
            item.parentCategoryNameEn?.toLowerCase().includes(normalizedSearch) ||
            item.parentCategoryNameAr?.includes(searchQuery);

        return matchesSearch;
    });

    const groupedFilteredItems = filteredItems.reduce((groups, item) => {
        const categoryLabel = getItemCategoryLabel(item);
        const existingGroup = groups.find((group) => group.label === categoryLabel);

        if (existingGroup) {
            existingGroup.items.push(item);
            return groups;
        }

        groups.push({
            label: categoryLabel,
            items: [item]
        });

        return groups;
    }, []);

    // Auto-populate customer form when phone number matches
    useEffect(() => {
        const phone = customerForm.mobileNumber.trim();
        if (phone.length >= 8) {
            // Check if we already have this user in our local allUsers list (optional optimization)
            const match = allUsers.find(u => u.phoneNumber === phone || (u.mobileNumber === phone));
            if (match) {
                setCustomerForm(prev => ({
                    ...prev,
                    firstName: match.firstName || "",
                    lastName: match.lastName || "",
                    emailAddress: match.emailAddress || "",
                    building: match.building || "",
                    road: match.road || "",
                    block: match.block || "",
                    districtArea: match.districtArea || match.districtName || "",
                    flatOfficeNo: match.flatOfficeNo || match.flat || "",
                    shortAddress: match.shortAddress || ""
                }));
            } else {
                // If not found in local state, we can call the search API
                const searchCustomer = async () => {
                    try {
                        const token = localStorage.getItem("token");
                        const selectedCountry = localStorage.getItem("selectedCountry");
                        const tenantId = localStorage.getItem("tenantId");

                        const response = await fetch(`${config.WS_BASE_URL}api/walk-in-customer/search?phone=${phone}`, {
                            headers: {
                                'Authorization': token || '',
                                'Country': selectedCountry || '1',
                                'Content-Type': 'application/json',
                                'X-Tenant-Key': tenantId || 'main'
                            }
                        });

                        if (response.ok) {
                            const found = await response.json();
                            if (found) {
                                setCustomerForm(prev => ({
                                    ...prev,
                                    firstName: found.firstName || "",
                                    lastName: found.lastName || "",
                                    emailAddress: found.emailAddress || "",
                                    building: found.building || "",
                                    road: found.road || "",
                                    block: found.block || "",
                                    districtArea: found.districtArea || "",
                                    flatOfficeNo: found.flatOfficeNo || "",
                                    shortAddress: found.shortAddress || ""
                                }));
                            }
                        }
                    } catch (err) {
                        console.error("Error searching customer:", err);
                    }
                };
                searchCustomer();
            }
        }
    }, [customerForm.mobileNumber, allUsers]);

    const handleConfirmOrder = async () => {
        if (!selectedCustomer) {
            alert("Please select or add a customer first");
            return;
        }
        if (basket.length === 0) {
            alert("Basket is empty");
            return;
        }

        try {
            setIsLoading(true);
            const token = localStorage.getItem("token");
            const selectedCountry = localStorage.getItem("selectedCountry");
            const tenantId = localStorage.getItem("tenantId");

            const orderData = {
                walkInCustomerId: selectedCustomer.id,
                subtotal: subtotal,
                discount: parseFloat(discount || 0),
                deliveryCharge: parseFloat(deliveryCharge || 0),
                total: grandTotal,
                createdBy: localStorage.getItem("username"),
                items: basket.map(item => {
                    const orderItem = {
                        serviceId: item.serviceId,
                        quantity: item.quantity,
                        price: item.price,
                        total: item.price * item.quantity
                    };

                    if (item.serviceTypeId != null) {
                        orderItem.serviceTypeId = item.serviceTypeId;
                    }

                    return orderItem;
                })
            };

            const response = await fetch(`${config.WS_BASE_URL}api/walk-in-order`, {
                method: 'POST',
                headers: {
                    'Authorization': token || '',
                    'Country': selectedCountry || '1',
                    'Content-Type': 'application/json',
                    'X-Tenant-Key': tenantId || 'main'
                },
                body: JSON.stringify(orderData)
            });

            if (response.ok) {
                alert("Order placed successfully!");
                setBasket([]);
                setSelectedCustomer(null);
                setSelectedAddress(null);
                setDiscount(0);
                setDeliveryCharge(0);
            } else {
                const result = await response.json();
                throw new Error(result.message || "Failed to place order");
            }
        } catch (error) {
            console.error("Error placing order:", error);
            alert(error.message);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSaveCustomer = async () => {
        try {
            const token = localStorage.getItem("token");
            const selectedCountry = localStorage.getItem("selectedCountry");
            const tenantId = localStorage.getItem("tenantId");

            const response = await fetch(`${config.WS_BASE_URL}api/walk-in-customer`, {
                method: 'POST',
                headers: {
                    'Authorization': token || '',
                    'Country': selectedCountry || '1',
                    'Content-Type': 'application/json',
                    'X-Tenant-Key': tenantId || 'main'
                },
                body: JSON.stringify(customerForm)
            });

            if (response.ok) {
                const userData = await response.json();
                setSelectedCustomer(userData);
                setSelectedAddress({
                    building: customerForm.building,
                    road: customerForm.road,
                    block: customerForm.block,
                    districtArea: customerForm.districtArea,
                    shortAddress: customerForm.shortAddress,
                    flatOfficeNo: customerForm.flatOfficeNo
                });
                setCustomerDialogOpen(false);
            } else {
                const result = await response.json();
                throw new Error(result.message || "Failed to save customer");
            }
        } catch (error) {
            console.error("Error saving customer:", error);
            alert(error.message);
        }
    };

    const handleItemClick = (item) => {
        if (getServiceTypesForItem(item).length === 0) {
            return;
        }
        setSelectedItem(item);
        setDialogOpen(true);
    };

    const handleServiceSelect = (item, serviceType) => {
        const selectedTypeId = serviceType.serviceTypeId ?? serviceType.id;
        const basketId = `${item.id}-${selectedTypeId}`;
        const cleanServiceName = getServiceTypeName(serviceType);
        const usesNewPricingStructure = Array.isArray(item?.servicePricings) && item.servicePricings.length > 0;
        const resolvedPrice = parsePrice(serviceType.price);

        setBasket(prev => {
            const existing = prev.find(i => i.basketId === basketId);
            if (existing) {
                return prev.map(i => i.basketId === basketId ? { ...i, quantity: i.quantity + 1 } : i);
            }
            return [...prev, {
                basketId,
                itemId: item.id,
                serviceId: usesNewPricingStructure ? item.id : serviceType.id,
                serviceTypeId: usesNewPricingStructure ? selectedTypeId : undefined,
                name: item.nameEn,
                serviceName: cleanServiceName,
                price: resolvedPrice,
                quantity: 1,
                image: item.imageUrl
            }];
        });
        setDialogOpen(false);
        setSelectedItem(null);
    };

    const updateQuantity = (basketId, delta) => {
        setBasket(prev => prev.map(i => {
            if (i.basketId === basketId) {
                const newQty = Math.max(1, i.quantity + delta);
                return { ...i, quantity: newQty };
            }
            return i;
        }));
    };

    const removeFromBasket = (basketId) => {
        setBasket(prev => prev.filter(i => i.basketId !== basketId));
    };

    const subtotal = basket.reduce((acc, item) => acc + (parseFloat(item.price || 0) * item.quantity), 0);
    const grandTotal = Math.max(0, subtotal - parseFloat(discount || 0) + parseFloat(deliveryCharge || 0));
    const selectedItemServiceTypes = getServiceTypesForItem(selectedItem);

    return (
        <Box style={{
            backgroundColor: "#f8f9fe",
            height: "100vh",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column"
        }}>
            <DoobiHeader section="InBuilt POS" subsection="Handle walk-in sales with Built-in POS System" />
            <Box style={{ flex: 1, position: 'relative', marginTop: '-6rem', overflow: 'hidden' }}>
                <Container maxWidth={false} component={Box}>
                    <Grid container spacing={3} alignItems="flex-start">
                        {/* LEFT COLUMN - Item Search & Selection */}
                        <Grid item xs={12} lg={8}>
                            <Card className={classes.fullHeightCard}>
                                <CardContent style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }}>
                                    {/* Search Field */}
                                    <TextField
                                        fullWidth
                                        variant="outlined"
                                        placeholder="Search items by name..."
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        className={classes.searchField}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <Search />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />

                                    <Typography variant="h6" component="h6" color="textSecondary" gutterBottom>
                                        Select Item ({filteredItems.length} items)
                                    </Typography>

                                    {/* Items Grid - Scrollable */}
                                    <Box className={classes.scrollableContent}>
                                        {isLoading ? (
                                            <Box display="flex" justifyContent="center" alignItems="center" minHeight="120px">
                                                <CircularProgress />
                                            </Box>
                                        ) : groupedFilteredItems.length > 0 ? groupedFilteredItems.map((group) => (
                                            <Box key={group.label} marginBottom="1.5rem">
                                                <Typography
                                                    variant="subtitle1"
                                                    color="textSecondary"
                                                    style={{ fontWeight: 700, textTransform: "uppercase", marginBottom: "0.75rem" }}
                                                >
                                                    {group.label}
                                                </Typography>
                                                <Grid container spacing={2}>
                                                    {group.items.map((item) => {
                                                        const serviceTypeLabels = getItemServiceTypeLabels(item);

                                                        return (
                                                            <Grid item xs={12} sm={6} md={4} key={item.id}>
                                                                <Card
                                                                    className={classes.itemCard}
                                                                    onClick={() => handleItemClick(item)}
                                                                    variant="outlined"
                                                                >
                                                                    <CardContent>
                                                                        <Box display="flex" alignItems="center">
                                                                            <img
                                                                                src={item.imageUrl}
                                                                                alt={item.nameEn}
                                                                                className={classes.itemIcon}
                                                                                onError={(e) => {
                                                                                    e.target.onerror = null;
                                                                                    e.target.src = "https://cdn-icons-png.flaticon.com/512/3003/3003984.png";
                                                                                }}
                                                                            />
                                                                            <Box>
                                                                                <Typography variant="body1" style={{ fontWeight: 600 }}>
                                                                                    {item.nameEn}
                                                                                </Typography>
                                                                                <Typography variant="body2" color="textSecondary">
                                                                                    {item.nameAr}
                                                                                </Typography>
                                                                                {serviceTypeLabels.length > 0 && (
                                                                                    <Typography variant="caption" color="textSecondary" style={{ display: "block", marginTop: "4px" }}>
                                                                                        {serviceTypeLabels.join(" • ")}
                                                                                    </Typography>
                                                                                )}
                                                                            </Box>
                                                                        </Box>
                                                                    </CardContent>
                                                                </Card>
                                                            </Grid>
                                                        );
                                                    })}
                                                </Grid>
                                            </Box>
                                        )) : (
                                            <Typography variant="body1" color={errorMsg ? "error" : "textSecondary"}>
                                                {errorMsg ? `Error: ${errorMsg}` : "No items found."}
                                            </Typography>
                                        )}
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>

                        {/* RIGHT COLUMN - Order Basket */}
                        <Grid item xs={12} lg={4}>
                            <Card className={classes.fullHeightCard}>
                                <CardContent style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }}>
                                    <Typography variant="h5" style={{ fontWeight: 700, marginBottom: '1.5rem' }}>Order Basket</Typography>

                                    {/* Modern Customer Selection */}
                                    <Box className={classes.selectionBlock} onClick={() => {
                                        if (selectedCustomer) {
                                            setCustomerForm({
                                                mobileNumber: selectedCustomer.mobileNumber || selectedCustomer.phoneNumber || "",
                                                firstName: selectedCustomer.firstName || "",
                                                lastName: selectedCustomer.lastName || "",
                                                emailAddress: selectedCustomer.emailAddress || "",
                                                building: selectedCustomer.building || "",
                                                road: selectedCustomer.road || "",
                                                block: selectedCustomer.block || "",
                                                districtArea: selectedCustomer.districtArea || selectedCustomer.districtName || "",
                                                flatOfficeNo: selectedCustomer.flatOfficeNo || selectedCustomer.flat || "",
                                                shortAddress: selectedCustomer.shortAddress || ""
                                            });
                                        } else {
                                            resetCustomerForm();
                                        }
                                        setCustomerDialogOpen(true);
                                    }}>
                                        <Box display="flex" alignItems="center">
                                            <Person color={selectedCustomer ? "primary" : "disabled"} style={{ marginRight: '12px' }} />
                                            <Box>
                                                <Typography variant="caption" color="textSecondary" style={{ display: 'block' }}>Customer Details</Typography>
                                                <Typography variant="body2" style={{ fontWeight: 600 }}>
                                                    {selectedCustomer ? `${selectedCustomer.firstName} ${selectedCustomer.lastName}` : "Add/Select Customer"}
                                                </Typography>
                                                {selectedCustomer && (
                                                    <Typography variant="caption" style={{ display: 'block', color: theme.palette.primary.main }}>
                                                        {selectedCustomer.mobileNumber || selectedCustomer.phoneNumber}
                                                        {selectedAddress && ` • ${selectedAddress.building}, ${selectedAddress.road}`}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Box>
                                        <IconButton size="small"><PersonAdd fontSize="small" /></IconButton>
                                    </Box>

                                    <Divider style={{ margin: '1rem 0' }} />

                                    {/* Basket Items - Scrollable */}
                                    <Box className={classes.scrollableContent}>
                                        {basket.length === 0 ? (
                                            <Typography variant="body2" color="textSecondary" align="center" style={{ padding: "40px 0" }}>
                                                Your basket is empty
                                            </Typography>
                                        ) : (
                                            basket.map((item) => (
                                                <Box key={item.basketId} className={classes.basketItem} display="flex" alignItems="center">
                                                    <Box flex={1}>
                                                        <Typography variant="body2" style={{ fontWeight: 600 }}>
                                                            {item.name}
                                                        </Typography>
                                                        <Typography variant="caption" color="textSecondary">
                                                            {item.serviceName}
                                                        </Typography>
                                                    </Box>

                                                    <Box display="flex" alignItems="center">
                                                        <IconButton size="small" className={classes.qtyBtn} onClick={() => updateQuantity(item.basketId, -1)}>
                                                            <Remove fontSize="small" />
                                                        </IconButton>
                                                        <Typography variant="body2">{item.quantity}</Typography>
                                                        <IconButton size="small" className={classes.qtyBtn} onClick={() => updateQuantity(item.basketId, 1)}>
                                                            <Add fontSize="small" />
                                                        </IconButton>
                                                    </Box>

                                                    <Box width="80px" textAlign="right">
                                                        <Typography variant="body2" style={{ fontWeight: 600 }}>
                                                            {(item.price * item.quantity).toFixed(2)} BD
                                                        </Typography>
                                                    </Box>

                                                    <IconButton size="small" color="secondary" onClick={() => removeFromBasket(item.basketId)} style={{ marginLeft: "8px" }}>
                                                        <DeleteOutline fontSize="small" />
                                                    </IconButton>
                                                </Box>
                                            ))
                                        )}
                                    </Box>

                                    {/* Totals Section */}
                                    <Box>
                                        <Box display="flex" justifyContent="space-between" marginBottom="0.8rem">
                                            <Typography style={{ fontWeight: 500 }}>Subtotal:</Typography>
                                            <Typography style={{ fontWeight: 600 }}>{subtotal.toFixed(2)} BD</Typography>
                                        </Box>

                                        <Box display="flex" justifyContent="space-between" alignItems="center" marginBottom="0.8rem">
                                            <Typography variant="body2">Discount (BD):</Typography>
                                            <TextField
                                                variant="outlined"
                                                size="small"
                                                type="number"
                                                value={discount}
                                                onChange={(e) => setDiscount(e.target.value)}
                                                style={{ width: "90px" }}
                                                inputProps={{ style: { padding: '8px', textAlign: 'right' } }}
                                            />
                                        </Box>

                                        <Box display="flex" justifyContent="space-between" alignItems="center" marginBottom="0.8rem">
                                            <Typography variant="body2">Delivery Charge (BD):</Typography>
                                            <TextField
                                                variant="outlined"
                                                size="small"
                                                type="number"
                                                value={deliveryCharge}
                                                onChange={(e) => setDeliveryCharge(e.target.value)}
                                                style={{ width: "90px" }}
                                                inputProps={{ style: { padding: '8px', textAlign: 'right' } }}
                                            />
                                        </Box>

                                        <Divider style={{ margin: "1rem 0" }} />

                                        <Box display="flex" justifyContent="space-between" alignItems="center" marginBottom="1.5rem">
                                            <Typography variant="h5" style={{ fontWeight: 700 }}>Total To Pay:</Typography>
                                            <Typography variant="h4" color="primary" style={{ fontWeight: 800 }}>
                                                {grandTotal.toFixed(2)} BD
                                            </Typography>
                                        </Box>

                                        <Button
                                            variant="contained"
                                            color="primary"
                                            fullWidth
                                            size="large"
                                            style={{ padding: '12px', fontWeight: 700, borderRadius: '10px' }}
                                            onClick={handleConfirmOrder}
                                            disabled={isLoading || basket.length === 0}
                                        >
                                            {isLoading ? <CircularProgress size={24} color="inherit" /> : "CONFIRM ORDER"}
                                        </Button>
                                    </Box>

                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                </Container>

                {/* Service Type Selection Dialog */}
                <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
                    <DialogTitle>
                        <Typography variant="h3" component="div" style={{ fontWeight: 700, textAlign: 'center', marginTop: '10px' }}>
                            {selectedItem?.nameEn}
                        </Typography>
                        <Typography variant="h5" color="textSecondary" style={{ textAlign: 'center', fontWeight: 500 }}>
                            {selectedItem?.nameAr}
                        </Typography>
                    </DialogTitle>
                    <DialogContent>
                        <Divider style={{ margin: '15px 0' }} />
                        <Typography variant="h6" color="textSecondary" align="center" gutterBottom>
                            Choose Service Type
                        </Typography>
                        <Box display="flex" flexWrap="wrap" justifyContent="center" marginTop="1.5rem" marginBottom="1.5rem">
                            {selectedItemServiceTypes.length > 0 ? selectedItemServiceTypes.map((serviceType) => {
                                const cleanName = getServiceTypeName(serviceType);
                                return (
                                    <Chip
                                        key={serviceType.id}
                                        label={`${cleanName} - ${parsePrice(serviceType.price).toFixed(2)} BD`}
                                        className={classes.serviceChip}
                                        color="primary"
                                        variant="outlined"
                                        onClick={() => handleServiceSelect(selectedItem, serviceType)}
                                    />
                                );
                            }) : (
                                <Typography variant="body2" color="textSecondary">
                                    No service type pricing is configured for this item.
                                </Typography>
                            )}
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setDialogOpen(false)} color="default">
                            Cancel
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Unified Customer & Address Dialog */}
                <Dialog open={customerDialogOpen} onClose={() => setCustomerDialogOpen(false)} maxWidth="md" fullWidth>
                    <DialogTitle>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="h4" style={{ fontWeight: 700 }}>Customer Information</Typography>
                        </Box>
                    </DialogTitle>
                    <DialogContent dividers>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                                <Typography variant="subtitle2" gutterBottom>Contact Details</Typography>
                                <TextField
                                    fullWidth
                                    label="Phone Number"
                                    variant="outlined"
                                    margin="dense"
                                    value={customerForm.mobileNumber}
                                    onChange={(e) => setCustomerForm({ ...customerForm, mobileNumber: e.target.value })}
                                    helperText="Type phone number to auto-populate existing customers"
                                />
                                <Box display="flex" mt={1}>
                                    <TextField
                                        fullWidth
                                        label="First Name"
                                        variant="outlined"
                                        margin="dense"
                                        value={customerForm.firstName}
                                        onChange={(e) => setCustomerForm({ ...customerForm, firstName: e.target.value })}
                                        style={{ marginRight: '8px' }}
                                    />
                                    <TextField
                                        fullWidth
                                        label="Last Name"
                                        variant="outlined"
                                        margin="dense"
                                        value={customerForm.lastName}
                                        onChange={(e) => setCustomerForm({ ...customerForm, lastName: e.target.value })}
                                    />
                                </Box>
                                <TextField
                                    fullWidth
                                    label="Email Address"
                                    variant="outlined"
                                    margin="dense"
                                    value={customerForm.emailAddress}
                                    onChange={(e) => setCustomerForm({ ...customerForm, emailAddress: e.target.value })}
                                />
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <Typography variant="subtitle2" gutterBottom>Address Details</Typography>
                                <Box display="flex">
                                    <TextField
                                        fullWidth
                                        label="Building"
                                        variant="outlined"
                                        margin="dense"
                                        value={customerForm.building}
                                        onChange={(e) => setCustomerForm({ ...customerForm, building: e.target.value })}
                                        style={{ marginRight: '8px' }}
                                    />
                                    <TextField
                                        fullWidth
                                        label="Road"
                                        variant="outlined"
                                        margin="dense"
                                        value={customerForm.road}
                                        onChange={(e) => setCustomerForm({ ...customerForm, road: e.target.value })}
                                    />
                                </Box>
                                <Box display="flex">
                                    <TextField
                                        fullWidth
                                        label="Block"
                                        variant="outlined"
                                        margin="dense"
                                        value={customerForm.block}
                                        onChange={(e) => setCustomerForm({ ...customerForm, block: e.target.value })}
                                        style={{ marginRight: '8px' }}
                                    />
                                    <TextField
                                        fullWidth
                                        label="District/Area"
                                        variant="outlined"
                                        margin="dense"
                                        value={customerForm.districtArea}
                                        onChange={(e) => setCustomerForm({ ...customerForm, districtArea: e.target.value })}
                                    />
                                </Box>
                                <TextField
                                    fullWidth
                                    label="Flat/Office No."
                                    variant="outlined"
                                    margin="dense"
                                    value={customerForm.flatOfficeNo}
                                    onChange={(e) => setCustomerForm({ ...customerForm, flatOfficeNo: e.target.value })}
                                />
                                <TextField
                                    fullWidth
                                    label="Short Address/Landmark"
                                    variant="outlined"
                                    margin="dense"
                                    value={customerForm.shortAddress}
                                    onChange={(e) => setCustomerForm({ ...customerForm, shortAddress: e.target.value })}
                                />
                            </Grid>
                        </Grid>
                    </DialogContent>
                    <DialogActions style={{ padding: '16px' }}>
                        <Button onClick={() => setCustomerDialogOpen(false)} color="default">Cancel</Button>
                        <Button
                            variant="contained"
                            color="primary"
                            disabled={!customerForm.mobileNumber || !customerForm.firstName}
                            onClick={handleSaveCustomer}
                        >
                            Confirm Customer Details
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
        </Box>
    );
}

export default Pos;
